import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { capitalize } from "lodash";
import { useEffect, useState } from "react";

import Flex from "../../../../../../components/Flex";
import LoadingOverlay from "../../../../../../components/LoadingOverlay";
import StyledFormModal from "../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../components/app/StyledButtonV2";
import CustomTitle from "../../../../../../components/app/CustomTitle";
import ProviderSelectScroll from "@/components/ProviderSelectScroll";
import { AsyncSelectOption } from "../../../../../../components/AsyncSelect";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../hooks/useSnackbar";
import { useGetAllScreeningListPrefill } from "../../../../../../queries/encounter/screening/use-getAllScreenerAns";
import { useAssignForReview } from "../../../../../../queries/patient-chart/assessment/assement-forms/use-assign-for-review";
import { usePatientAssessmentExport } from "../../../../../../queries/patient-chart/assessment/use-patient-assessment-export";
import {
  showToastOnApiCall,
  getAge,
  getXtenantId,
} from "../../../../../../utils/utils";
import { ApiQuestionnaireItem } from "./Assement-constant";
import Text from "../../../../../../components/Text";
import { formatDateTime } from "../../../../../../utils/date-time-utility";
import { useDashboardPatientQuestionaireStatusUpdate } from "../../../../../../queries/dashboard/use-dashboard-patient-questionaire-status-update";
import { useResolveRequiringAction } from "../../../../../../queries/patient-chart/requiring-action/use-resolve-requiring-action";
import NewAppointment from "../../../scheduling/appointment/NewAppointment";
import FreeTextScreeningForm from "../../../scheduling/appointment/encounter/Assessment/FreeTextScreeningForm";
import { screenerAnswerText } from "../../../scheduling/appointment/encounter/Assessment/free-text-screening";
import { useFreeTextScreener } from "../../../scheduling/appointment/encounter/Assessment/hooks/use-free-text-screener";
import { useScreeningList } from "@/queries/encounter/screening/use-screening-get-list";
import { useScreeningUpdate } from "@/queries/encounter/screening/use-screening-update";
import { DEF_PAGINATION } from "@/utils/utils";
import { SCREENING_PREFILL_PAGE_SIZE } from "@/pages/app/provider/scheduling/appointment/encounter/Assessment/assessment-constant";
interface AssessmentPreviewDrawerProps {
  disclosureHost: DisclosureHost;
  appointmentIdParam?: string;
  requiringActionId?: string;
  questionnaire?: ApiQuestionnaireItem;
  allowReview?: boolean;
  enableAssignForReview?: boolean;
  enableSchedule?: boolean;
  enablePrint?: boolean;
}

interface DataItem {
  question: string;
  questionId: string;
  type: string;
  uuid?: string;
  answer?: string;
  answerString?: string;
  score?: number;
}

interface DetailedResponsesProps {
  data: DataItem[];
}

const ANSWER_LABELS: Record<string, string> = {
  Unknown: "-",
  True: "Yes",
  False: "No",
};

const DetailedResponses = ({ data }: DetailedResponsesProps) => {
  return (
    <Box sx={{ width: "100%" }}>
      <Text
        sx={{
          fontSize: "18px",
          fontWeight: 600,
        }}
      >
        Detailed Responses
      </Text>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          width: "100%",
        }}
      >
        {data?.map((item, index) => (
          <Box
            key={item.uuid || index}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "16px",
              width: "100%",
              padding: "12px 0",
              borderBottom:
                index < data.length - 1 ? "1px solid #E0E0E0" : "none",
            }}
          >
            {/* Question Column */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                paddingRight: "16px",
              }}
            >
              <Text c="#333">{item.question}</Text>
            </Box>

            {/* Response Column */}
            <Box
              sx={{
                flex: "0 0 180px",
                minWidth: 0,
                paddingRight: "16px",
              }}
            >
              <Text c="#333">
                {ANSWER_LABELS[item.answer ?? ""] ?? screenerAnswerText(item)}
              </Text>
            </Box>

            {/* Score Column */}
            {/* <Box
              sx={{
                flex: "0 0 100px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Chip
                label={`Score: ${item.score ?? 0}`}
                sx={{
                  backgroundColor: "#F5F5F5",
                  border: "1px solid #E0E0E0",
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#424242",
                  height: "auto",
                  padding: "4px 12px",
                  "& .MuiChip-label": {
                    padding: 0,
                  },
                }}
              />
            </Box> */}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const AssessmentPreviewDrawer = ({
  disclosureHost,
  questionnaire,
  allowReview,
  requiringActionId,
  enableAssignForReview = false,
  enableSchedule = false,
  enablePrint = false,
}: AssessmentPreviewDrawerProps) => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const newAppointmentDisclosure = useDisclosure();
  const [assignedProvider, setAssignedProvider] =
    useState<AsyncSelectOption | null>(null);

  useEffect(() => {
    if (!disclosureHost.opened) {
      setAssignedProvider(null);
    }
  }, [disclosureHost.opened]);

  const { mutateAsync: updateQuestionnaireStatus } =
    useDashboardPatientQuestionaireStatusUpdate();
  const { mutateAsync: resolveRequiringAction } = useResolveRequiringAction();
  const { mutateAsync: assignForReview } = useAssignForReview();
  const { mutateAsync: exportAssessment } = usePatientAssessmentExport();
  const { mutateAsync: updateScreeningAns } = useScreeningUpdate();

  const { data: prefillData, isLoading: isPrefillDataLoading } =
    useGetAllScreeningListPrefill({
      type: questionnaire?.screenerType as string,
      patientId: questionnaire?.appointmentId
        ? undefined
        : questionnaire?.patientId,
      pagination: {
        page: 0,
        pageSize: SCREENING_PREFILL_PAGE_SIZE,
      },
      gridSorting: [],
      appointmentUuid: questionnaire?.appointmentId,
      xTENANTID: getXtenantId(),
    });

  const saveProviderSection = () => {
    if (
      !providerSection.def ||
      !questionnaire?.patientId ||
      providerSection.isEmpty
    )
      return;
    if (providerSection.issue) {
      showSnackbar({ message: providerSection.issue, type: "error" });
      return;
    }
    showToastOnApiCall({
      apiCall: updateScreeningAns({
        patientUuid: questionnaire.patientId,
        xTENANTID: getXtenantId(),
        patientScreener: {
          screenerQuestion: providerSection.toRows() as any,
          appointmentId: questionnaire.appointmentId || (undefined as any),
          screenerType: questionnaire.screenerType as any,
          submittedBy: "PROVIDER",
        },
      }),
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["encounter-all=screening-list"],
        });
      },
      showSnackbar,
    });
  };

  // Free-text screeners have provider-only questions (e.g. obesity counseling)
  // that the provider fills in here, saved as answer rows like the patient's.
  const { data: screenerQuestions } = useScreeningList({
    pagination: DEF_PAGINATION,
    gridSorting: [],
    type: questionnaire?.screenerType ?? "",
    xTENANTID: getXtenantId(),
  });
  const providerSection = useFreeTextScreener(
    questionnaire?.screenerType,
    screenerQuestions?.data?.screeners,
    prefillData?.data?.content,
    "PROVIDER"
  );

  function handleClose() {
    disclosureHost.close();
  }

  const handleAssignProvider = (option: AsyncSelectOption | null) => {
    setAssignedProvider(option);
    if (!option?.value || !questionnaire?.uuid) return;

    showToastOnApiCall({
      apiCall: assignForReview({
        assessmentId: questionnaire.uuid,
        providerId: option.value,
      }),
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["dashboard-patient-questionaire"],
        });
      },
      showSnackbar,
    });
  };

  const handlePrint = () => {
    if (!questionnaire?.uuid) return;

    exportAssessment({
      exportType: "pdf",
      questionnaireIds: [questionnaire.uuid],
    }).then((resp) => {
      const blobFile = new Blob([resp.data as BlobPart], {
        type: "application/pdf",
      });
      const fileUrl = URL.createObjectURL(blobFile);
      const printWindow = window.open(fileUrl);
      if (printWindow) {
        printWindow.addEventListener("load", () => printWindow.print());
      }
    });
  };

  const reviewHandlerForRequiringAction = () => {
    if (!questionnaire?.uuid) return;
    if (requiringActionId) {
      showToastOnApiCall({
        apiCall: resolveRequiringAction({
          requiringActionId: requiringActionId,
        }),
        onSave: () => {
          queryClient.invalidateQueries({
            queryKey: ["requiring-action"],
          });
          queryClient.invalidateQueries({
            queryKey: ["patient-requiring-action"],
          });
          handleClose();
        },
        showSnackbar,
      });
    } else {
      showToastOnApiCall({
        apiCall: updateQuestionnaireStatus({
          id: questionnaire.uuid,
          status: true,
          xTENANTID: getXtenantId(),
        }),
        onSave: () => {
          queryClient.invalidateQueries({
            queryKey: ["dashboard-patient-questionaire"],
          });
          handleClose();
        },
        showSnackbar,
      });
    }
  };

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={handleClose}
      dialogTitle="Patient Assessment Review"
      dialogAction={
        enablePrint || enableSchedule || allowReview ? (
          <Flex gap={1}>
            {enablePrint && (
              <StyledButtonV2
                label="Print"
                variant="outlined"
                onClick={handlePrint}
              />
            )}
            {enableSchedule && (
              <StyledButtonV2
                label="Schedule Appointment"
                variant="outlined"
                onClick={() =>
                  newAppointmentDisclosure.open({
                    value: questionnaire?.patientId,
                    label: questionnaire?.patientName,
                  })
                }
              />
            )}
            {allowReview && (
              <StyledButtonV2
                label="Mark as Reviewed"
                variant="contained"
                onClick={reviewHandlerForRequiringAction}
              />
            )}
          </Flex>
        ) : undefined
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "50vw",
          height: "80vh",
        },
      }}
    >
      <NewAppointment
        prefillPatientData={newAppointmentDisclosure?.data}
        opened={newAppointmentDisclosure.opened}
        onClose={newAppointmentDisclosure.close}
      />
      <LoadingOverlay isLoading={isPrefillDataLoading} />
      <Flex
        flexDirection="column"
        gap={2}
        width="100%"
        sx={{ overflow: "auto" }}
      >
        {/* Patient Information Section */}
        <Box
          sx={{
            padding: "12px 16px",
            borderRadius: "4px",
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <CustomTitle bold>Patient Information</CustomTitle>
          <Grid container spacing={1.5} mt={0.5}>
            <Grid item xs={12} sm={6}>
              <Text fontWeight="medium">
                <span style={{ color: "#999" }}>Patient Name:</span>{" "}
                <strong>{questionnaire?.patientName ?? "-"}</strong>
              </Text>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text fontWeight="medium">
                <span style={{ color: "#999" }}>MRN:</span>{" "}
                <strong>{questionnaire?.patientMrn ?? "-"}</strong>
              </Text>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text fontWeight="medium">
                <span style={{ color: "#999" }}>Age:</span>{" "}
                <strong>
                  {questionnaire?.patientDob
                    ? `${getAge(questionnaire.patientDob)} yrs`
                    : "-"}
                </strong>
              </Text>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text fontWeight="medium">
                <span style={{ color: "#999" }}>Submitted On:</span>{" "}
                <strong>
                  {questionnaire?.actionDate
                    ? formatDateTime(questionnaire.actionDate)
                    : "-"}
                </strong>
              </Text>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text fontWeight="medium">
                <span style={{ color: "#999" }}>Submitted By:</span>{" "}
                <strong>
                  {questionnaire?.submittedBy
                    ? capitalize(questionnaire.submittedBy)
                    : "-"}
                </strong>
              </Text>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text fontWeight="medium">
                <span style={{ color: "#999" }}>Assessment Type:</span>{" "}
                <strong>{questionnaire?.screenerType ?? "-"}</strong>
              </Text>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text fontWeight="medium">
                <span style={{ color: "#999" }}>Score:</span>{" "}
                <strong>
                  {`${questionnaire?.score ?? 0} / ${
                    questionnaire?.totalScore ?? 0
                  }`}
                </strong>
              </Text>
            </Grid>
          </Grid>
        </Box>

        {enableAssignForReview && (
          <Box
            sx={{
              padding: "12px 16px",
              borderRadius: "4px",
              backgroundColor: "#fff",
              border: "1px solid #e0e0e0",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <ProviderSelectScroll
              label="Assign for Review"
              value={assignedProvider}
              onChange={handleAssignProvider}
            />
          </Box>
        )}

        <Box
          sx={{
            padding: "16px",
            borderRadius: "4px",
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
            width: "100%",
            boxSizing: "border-box",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {/* Header Row: Title and Severity Badge */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
              width: "100%",
              gap: "8px",
              minWidth: 0,
            }}
          >
            <Text
              c="#333"
              sx={{
                fontWeight: 500,
              }}
            >
              {questionnaire?.screenerType}
            </Text>
          </Box>

          {/* Score and Progress Bar Row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              width: "100%",
              minWidth: 0,
            }}
          >
            {/* Score Display */}
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: "4px",
                flexShrink: 0,
              }}
            >
              <Text
                c="#333"
                sx={{
                  fontSize: "32px",
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {questionnaire?.score ?? "0"}
              </Text>
              <Text
                c="#999"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                }}
              >
                /{questionnaire?.totalScore ?? "0"}
              </Text>
            </Box>

            {/* Progress Bar */}
            <Box
              sx={{
                flex: 1,
                height: "8px",
                backgroundColor: "#E0E0E0",
                borderRadius: "4px",
                overflow: "hidden",
                position: "relative",
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  /* A missing or zero total renders an empty bar. Dividing by it
                     would emit an invalid width of NaN% or Infinity%. */
                  width: `${
                    Number(questionnaire?.totalScore) > 0
                      ? (Number(questionnaire?.score ?? 0) /
                          Number(questionnaire?.totalScore)) *
                        100
                      : 0
                  }%`,
                  backgroundColor: "#424242",
                  borderRadius: "4px",
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Detailed Responses Section */}
        <Box
          sx={{
            padding: "16px",
            borderRadius: "4px",
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
            width: "100%",
            boxSizing: "border-box",
            flexShrink: 0,
          }}
        >
          <DetailedResponses data={prefillData?.data?.content || []} />
        </Box>

        {providerSection.def && (
          <Box
            sx={{
              padding: "16px",
              borderRadius: "4px",
              backgroundColor: "#fff",
              border: "1px solid #e0e0e0",
              width: "100%",
              boxSizing: "border-box",
              flexShrink: 0,
            }}
          >
            <CustomTitle bold>Counseling and setting</CustomTitle>
            <Box mt={1}>
              <FreeTextScreeningForm
                def={providerSection.def}
                questions={screenerQuestions?.data?.screeners ?? []}
                values={providerSection.values}
                onChange={providerSection.setValue}
                audience="PROVIDER"
                disabled={!allowReview}
              />
            </Box>
            {allowReview && (
              <Flex justifyContent="flex-end" mt={1.5}>
                <StyledButtonV2
                  label="Save Counseling"
                  variant="contained"
                  onClick={saveProviderSection}
                />
              </Flex>
            )}
          </Box>
        )}
      </Flex>
    </StyledFormModal>
  );
};

export default AssessmentPreviewDrawer;
